import { camelCase } from 'lodash';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import Ad from '../../../../models/banner/Ad';
import { modelOf } from '../../../../prop-types';
import UIStore from '../../../../store/UIStore';
import ConfigStore from '../../../../store/ConfigStore';
import AdZones from '../../../../types/AdZones';
import BannerType from '../../../../types/banner/BannerType';
import BannerVideoType from '../../../../types/banner/BannerVideoType';
import {
  countImageSizeForSlotHeight,
  getConfiguredSlotCrossAxisSize,
  resolveBannerImageDimensions,
  resolveBannerOverlayMainAxisSize,
  resolveBannerSlideMainAxisSize,
} from '../../bannerMedia';

// Mobile Aspect ratio 1:1. e.g. 250x250px or as close as possible.
const MOBILE_ASPECT_RATIO = 1;
const VIDEO_ASPECT_RATIO = 16 / 9;

/**
 *
 * @param {number} crossAxisSize
 * @param {number} localAspectRatio
 * @returns {number} main axis size
 */
export const countMainAxisSize = (crossAxisSize, localAspectRatio) =>
  crossAxisSize / localAspectRatio;

/**
 *
 * @param {number} localMaximumCrossAxisSize
 * @param {number} axisSize
 * @returns {number} min cross axis size
 */
export const countMinCrossAxisSize = (localMaximumCrossAxisSize, axisSize) =>
  Math.min(localMaximumCrossAxisSize, axisSize);

const PRODUCT_PAGE_BANNER_ZONES = new Set([
  AdZones.PRODUCT_PAGE_BANNER_1,
  AdZones.PRODUCT_PAGE_BANNER_2,
  AdZones.PRODUCT_PAGE_BANNER_3,
  AdZones.PRODUCT_PAGE_BANNER_4,
]);

export const usesConfiguredSlotHeight = (adZone) =>
  adZone === AdZones.MAIN_BANNER ||
  adZone === AdZones.FOOTER_BANNER ||
  PRODUCT_PAGE_BANNER_ZONES.has(adZone);

export const isFileVideoBanner = (banner) =>
  banner.type === BannerType.VIDEO &&
  banner.video_embed_style === BannerVideoType.FILE;

const usesVideoSlotLetterboxing = (adZone) => usesConfiguredSlotHeight(adZone);

const getFileVideoPosterSizeAtSlot = (
  posterWidth,
  posterHeight,
  crossAxisSize,
  localElementWidth
) => {
  if (!posterWidth || !posterHeight) {
    return null;
  }

  const scale = Math.min(
    localElementWidth / posterWidth,
    crossAxisSize / posterHeight,
    1
  );

  return {
    width: Math.round(posterWidth * scale),
    height: Math.round(posterHeight * scale),
  };
};

export const BannerSliderStyles = ({
  configStore,
  uiStore,
  bannerSliderId,
  bannerSlider,
  elementWidth,
  maximumSlideWidth,
  slides,
  aspectRatio,
  maximumCrossAxisSize,
  adZone,
  ifVertical,
  slidesToShow,
  slideContentJustify = 'center',
  // BOTH peeks: size each .slick-slide to the creative (when multi-slide).
  // INFINITE / CENTERED: keep the slide at the full track-safe width so
  // empty side space lives inside the slide and contentJustify can align it.
  useCreativeSlideWidth = false,
  // Intrinsic sizes for null API image_width/height (measured after img load).
  measuredImageSizes = {},
}) => {
  const isConfiguredSlotHeight = usesConfiguredSlotHeight(adZone);
  const isVideoSlotLetterboxing = usesVideoSlotLetterboxing(adZone);
  const contentJustify = slideContentJustify || 'center';
  const getBannerMobileCssClasses = () => {
    const useMobileSquareMode = shouldUseMobileSquareMode();

    const bannerWrapper = useMobileSquareMode
      ? '.Banner__wrapper-mobile'
      : '.Banner__wrapper';

    const bannerBackgroundImage = useMobileSquareMode
      ? '.Banner__background-mobile-image'
      : '.Banner__background-image';

    return { bannerWrapper, bannerBackgroundImage };
  };

  const shouldUseMobileSquareMode = () =>
    hasVisibleMobileImageBanner.length >= 1 ||
    hasVisiblePortraitFileVideo.length >= 1;

  const getAspectRatio = () =>
    shouldUseMobileSquareMode() ? MOBILE_ASPECT_RATIO : aspectRatio;

  const getMaximumHeight = () =>
    shouldUseMobileSquareMode() ? elementWidth : maximumCrossAxisSize;

  const usesBannerScentSlot = () =>
    adZone === AdZones.MAIN_BANNER || adZone === AdZones.FOOTER_BANNER;

  const shouldRemoveScentHeight = () => {
    return (
      usesBannerScentSlot() &&
      !!configStore.banner.scent.pattern &&
      !(uiStore.isMobile && !configStore.banner.scent.shouldShowMobile) &&
      configStore.banner.scent.onTop
    );
  };

  const getScentHeight = () => {
    if (!usesBannerScentSlot()) {
      return 0;
    }

    return configStore.getScentHeight(uiStore.isMobile);
  };

  const getContentScentHeight = () => {
    return shouldRemoveScentHeight() ? configStore.banner.scent.height : 0;
  };

  const getMainAxis = () => (ifVertical ? 'height' : 'width');

  const getCrossAxis = () => (ifVertical ? 'width' : 'height');

  const getCrossAxisSize = () =>
    bannerSlider && bannerSlider[camelCase('client ' + getCrossAxis())];

  const countMainAxisDefaultSize = useCallback(
    (crossAxisSize, localAspectRatio) => crossAxisSize * localAspectRatio,
    [slides]
  );

  const hasVisibleMobileImageBanner = useMemo(() => {
    return [...slides].filter(
      (banner) => uiStore.isSmallest && banner.mobile_image
    );
  }, [slides, uiStore.isSmallest]);

  const hasVisiblePortraitFileVideo = useMemo(() => {
    return [...slides].filter(
      (banner) =>
        uiStore.isSmallest &&
        banner.type === BannerType.VIDEO &&
        banner.video_embed_style === BannerVideoType.FILE &&
        banner.video_poster_height > banner.video_poster_width
    );
  }, [slides, uiStore.isSmallest]);

  const isPortraitFileVideoBanner = (banner) =>
    uiStore.isSmallest &&
    banner.video_poster_height > banner.video_poster_width;

  const getFileVideoSizeAtSlot = (banner, crossAxisSize, localElementWidth) =>
    getFileVideoPosterSizeAtSlot(
      banner.video_poster_width,
      banner.video_poster_height,
      crossAxisSize,
      localElementWidth
    );

  const getFileVideoPosterSizedCss = (
    crossAxis,
    crossAxisSize,
    mainAxis,
    localElementWidth
  ) =>
    slides
      .filter(
        (banner) =>
          banner.type === BannerType.VIDEO &&
          banner.video_embed_style === BannerVideoType.FILE
      )
      .map((banner) => {
        if (isPortraitFileVideoBanner(banner)) {
          return '';
        }

        const size = getFileVideoSizeAtSlot(
          banner,
          crossAxisSize,
          localElementWidth
        );

        if (!size) {
          return '';
        }

        const videoFitCss = isVideoSlotLetterboxing
          ? `
          object-fit: contain;
          object-position: center;`
          : '';

        const containerExtraCss = isVideoSlotLetterboxing
          ? `
          aspect-ratio: auto;`
          : '';

        return `
        #${bannerSliderId} .Banner[data-banner-id="${banner.id}"] .Banner__video-container--file {
          ${crossAxis}: ${size.height}px;
          ${mainAxis}: ${size.width}px;${containerExtraCss}
        }
        #${bannerSliderId} .Banner[data-banner-id="${banner.id}"] .Banner__video-container--file video {${videoFitCss}
        }`;
      })
      .join('\n');

  /**
   * Main/footer image slides fill the computed slot height. Overflow is clipped
   * by the slide; images are height-driven so width follows aspect ratio and is
   * not capped at the image's intrinsic pixel width.
   *
   * Slides are top-aligned (not vertically centered) so content cannot shift
   * into MainBanner's 2px top margin/padding and leave a matching gap at the
   * bottom of the slider.
   *
   * The slot containers reserve room for the scent strip below the media
   * (scentHeight is 0 when there is no scent or the scent is drawn on top of
   * the media), while the media itself stays at the banner height.
   */
  const getConfiguredSlotCss = (
    crossAxis,
    crossAxisSize,
    mainAxis,
    scentHeight
  ) => {
    if (!isConfiguredSlotHeight) {
      return '';
    }

    const slotCrossAxisSize = getConfiguredSlotCrossAxisSize(
      crossAxisSize,
      scentHeight
    );

    return `
        #${bannerSliderId} .slick-list,
        #${bannerSliderId} .slick-track {
          ${crossAxis}: ${slotCrossAxisSize}px;
          min-${crossAxis}: ${slotCrossAxisSize}px;
        }
        /* Do not set display:flex on .slick-slide — that clears slick's
           float:left and stacks slides under the list (product multi-ad). */
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) {
          ${crossAxis}: ${slotCrossAxisSize}px;
          overflow: hidden;
          margin: 0;
          padding: 0;
        }
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) .BannerSlider__slide {
          /* position:relative so absolute .Banner can center (incl. overflow).
             Flex justify-content + overflow:hidden clips wide items to the start. */
          position: relative;
          display: block;
          width: 100%;
          box-sizing: border-box;
          ${crossAxis}: ${slotCrossAxisSize}px;
          overflow: hidden;
          max-${mainAxis}: none;
          margin: 0;
          padding: 0;
        }
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) .Banner:not(.Banner--file-video) {
          position: absolute;
          top: 0;
          /* Center in the visible slide; equal crop when creative is wider. */
          left: ${contentJustify === 'center' ? '50%' : '0'};
          transform: ${
            contentJustify === 'center' ? 'translateX(-50%)' : 'none'
          };
          display: block;
          width: fit-content;
          max-width: none;
          ${crossAxis}: ${slotCrossAxisSize}px;
          margin: 0;
          padding: 0;
        }
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) .Banner:not(.Banner--file-video) .Banner__wrapper,
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) .Banner:not(.Banner--file-video) .Banner__wrapper-mobile,
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) .Banner:not(.Banner--file-video) .MaybeExternalLink,
        #${bannerSliderId} .slick-slide:not(:has(.Banner--file-video)) .Banner:not(.Banner--file-video) picture {
          display: block;
          ${crossAxis}: ${crossAxisSize}px;
          margin: 0;
          padding: 0;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner:not(.Banner--file-video) .Banner__background-image,
        #${bannerSliderId} .BannerSlider__slide .Banner:not(.Banner--file-video) .Banner__background-mobile-image {
          display: block;
          ${crossAxis}: ${crossAxisSize}px;
          width: auto;
          max-width: none;
          object-fit: unset;
          vertical-align: top;
        }
      `;
  };

  /**
   * Image slides always fill the slot height (no top/bottom letterboxing).
   * Width follows aspect ratio and may overflow the slide; overflow is clipped
   * at the sides. Cap the slick-slide to the track-safe max (normally the full
   * measured list width when centerPadding is 0) so the creative reaches the
   * viewport edge. FILE videos may letterbox.
   */
  const getImageSlideWidthCss = (
    crossAxis,
    crossAxisSize,
    mainAxis,
    contentScentHeight,
    localMaximumSlideWidth
  ) => {
    if (!isConfiguredSlotHeight) {
      return '';
    }

    return slides
      .filter((banner) => banner.type !== BannerType.VIDEO)
      .map((banner) => {
        const useMobileImage = uiStore.isSmallest && !!banner.mobile_image;
        const { width: imageWidth, height: imageHeight } =
          resolveBannerImageDimensions(
            banner,
            useMobileImage,
            measuredImageSizes
          );
        const size = countImageSizeForSlotHeight(
          imageWidth,
          imageHeight,
          crossAxisSize
        );
        const slideSelector =
          `#${bannerSliderId} .slick-slide:has(` +
          `.Banner[data-banner-id="${banner.id}"])`;

        // SVGs / assets without API dimensions and not yet measured:
        // variableWidth needs an explicit .slick-slide width (list width).
        // Media keeps height + width:auto (no cover-zoom). After measure,
        // the aspect-driven path below runs like dimensioned images (BOTH peeks).
        if (!size) {
          const listWidth = elementWidth ? Math.floor(elementWidth) : 0;
          if (!listWidth || !crossAxisSize) {
            return '';
          }

          const slideMainAxisSize = listWidth;

          return `
        ${slideSelector} {
          ${mainAxis}: ${slideMainAxisSize}px;
        }
        ${slideSelector} .Banner:not(.Banner--file-video) {
          ${mainAxis}: auto;
          max-${mainAxis}: none;
          width: fit-content;
        }
        ${slideSelector} .Banner__wrapper,
        ${slideSelector} .Banner__wrapper-mobile,
        ${slideSelector} .Banner__background-image,
        ${slideSelector} .Banner__background-mobile-image {
          ${crossAxis}: ${crossAxisSize}px;
          ${mainAxis}: auto;
          min-${mainAxis}: 0;
          max-width: none;
          object-fit: unset;
        }
        ${slideSelector} .Banner__overlay {
          ${crossAxis}: ${crossAxisSize - contentScentHeight}px;
          ${mainAxis}: 100%;
          max-${mainAxis}: 100%;
          min-${mainAxis}: 0;
          left: 50%;
          right: auto;
          transform: translateX(-50%);
        }`;
        }

        const slideMainAxisSize = resolveBannerSlideMainAxisSize({
          creativeWidth: size.width,
          listWidth: elementWidth,
          trackSafeMax: localMaximumSlideWidth,
          useCreativeSlideWidth,
        });
        const overlayMainAxisSize = resolveBannerOverlayMainAxisSize(
          size.width,
          slideMainAxisSize
        );

        return `
        ${slideSelector} {
          ${mainAxis}: ${slideMainAxisSize}px;
        }
        ${slideSelector} .Banner:not(.Banner--file-video) {
          ${mainAxis}: ${size.width}px;
          max-${mainAxis}: none;
        }
        ${slideSelector} .Banner__wrapper,
        ${slideSelector} .Banner__wrapper-mobile,
        ${slideSelector} .Banner__background-image,
        ${slideSelector} .Banner__background-mobile-image {
          ${crossAxis}: ${size.height}px;
          ${mainAxis}: ${size.width}px;
          min-${mainAxis}: ${size.width}px;
          max-width: none;
        }
        ${slideSelector} .Banner__overlay {
          /* Visible creative box (centered on the banner), not full cropped art. */
          ${crossAxis}: ${size.height - contentScentHeight}px;
          ${mainAxis}: ${overlayMainAxisSize}px;
          max-${mainAxis}: ${overlayMainAxisSize}px;
          min-${mainAxis}: 0;
          left: 50%;
          right: auto;
          transform: translateX(-50%);
        }`;
      })
      .join('\n');
  };

  /**
   * LINK/embed videos have no intrinsic width. Give each slide a track-safe
   * width and a 16:9 box at slot height so variableWidth does not measure 0
   * and wrap the slide under the list.
   */
  const getLinkVideoSlideWidthCss = (
    crossAxis,
    crossAxisSize,
    mainAxis,
    localMaximumSlideWidth
  ) => {
    if (!isConfiguredSlotHeight || !crossAxisSize) {
      return '';
    }

    const embedVideoMainAxisSize = Math.round(
      crossAxisSize * VIDEO_ASPECT_RATIO
    );
    const slideMainAxisSize = resolveBannerSlideMainAxisSize({
      creativeWidth: embedVideoMainAxisSize,
      listWidth: elementWidth,
      trackSafeMax: localMaximumSlideWidth,
      useCreativeSlideWidth,
    });

    return slides
      .filter(
        (banner) =>
          banner.type === BannerType.VIDEO && !isFileVideoBanner(banner)
      )
      .map((banner) => {
        const slideSelector =
          `#${bannerSliderId} .slick-slide:has(` +
          `.Banner[data-banner-id="${banner.id}"])`;

        return `
        ${slideSelector} {
          ${mainAxis}: ${slideMainAxisSize}px;
        }
        ${slideSelector} .Banner__video-container:not(.Banner__video-container--file) {
          position: relative;
          ${crossAxis}: ${crossAxisSize}px;
          ${mainAxis}: ${embedVideoMainAxisSize}px;
        }`;
      })
      .join('\n');
  };

  /**
   * Letterboxed FILE videos share the image slot geometry: the slide reserves
   * room for the scent strip below the media and is top-aligned, so the scent
   * lines up with the neighbouring image slides instead of sitting higher and
   * pushing the media past the top of the slider.
   */
  const getFileVideoSlideCss = (
    crossAxis,
    crossAxisSize,
    mainAxis,
    localElementWidth,
    scentHeight
  ) => {
    const perBannerSlideCss = slides
      .filter(
        (banner) =>
          banner.type === BannerType.VIDEO &&
          banner.video_embed_style === BannerVideoType.FILE &&
          !isPortraitFileVideoBanner(banner)
      )
      .map((banner) => {
        const size = getFileVideoSizeAtSlot(
          banner,
          crossAxisSize,
          localElementWidth
        );

        if (!size) {
          return '';
        }

        const slotCrossAxisSize = isConfiguredSlotHeight
          ? getConfiguredSlotCrossAxisSize(crossAxisSize, scentHeight)
          : crossAxisSize;
        const slideCrossAxis = isVideoSlotLetterboxing
          ? slotCrossAxisSize
          : size.height;
        const slideMainAxis = resolveBannerSlideMainAxisSize({
          creativeWidth: size.width,
          listWidth: elementWidth,
          trackSafeMax: localElementWidth,
          useCreativeSlideWidth,
        });
        const slideAlignItems = isVideoSlotLetterboxing
          ? 'flex-start'
          : 'stretch';
        const fillSlideCss = isVideoSlotLetterboxing
          ? `
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .Banner--file-video {
          ${crossAxis}: ${slotCrossAxisSize}px;
        }
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .Banner__wrapper,
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .Banner__wrapper-mobile,
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .MaybeExternalLink {
          width: fit-content;
          ${crossAxis}: ${crossAxisSize}px;
          max-width: 100%;
          align-items: center;
        }`
          : '';

        return `
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) {
          ${crossAxis}: ${slideCrossAxis}px;
          ${mainAxis}: ${slideMainAxis}px;
          overflow: hidden;
        }
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .BannerSlider__slide {
          display: flex;
          align-items: ${slideAlignItems};
          justify-content: ${contentJustify};
          height: 100%;
          width: 100%;
        }${fillSlideCss}`;
      })
      .join('\n');

    const landscapeFileVideoLayoutCss = isVideoSlotLetterboxing
      ? ''
      : `
        #${bannerSliderId} .BannerSlider__slide .Banner--file-video:not(.Banner--file-video-portrait) {
          width: fit-content;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner--file-video:not(.Banner--file-video-portrait) .Banner__wrapper,
        #${bannerSliderId} .BannerSlider__slide .Banner--file-video:not(.Banner--file-video-portrait) .Banner__wrapper-mobile {
          width: fit-content;
          max-width: none;
        }`;

    return `
        ${perBannerSlideCss}${landscapeFileVideoLayoutCss}
      `;
  };

  const getPortraitFileVideoSquareSlotCss = (
    crossAxis,
    crossAxisSize,
    mainAxis
  ) => {
    if (!uiStore.isSmallest) {
      return '';
    }

    const portraitWrapper = shouldUseMobileSquareMode()
      ? '.Banner__wrapper-mobile'
      : '.Banner__wrapper';

    return `
        #${bannerSliderId} .BannerSlider__slide .Banner--file-video-portrait ${portraitWrapper} {
          ${crossAxis}: ${crossAxisSize}px;
          min-${mainAxis}: ${crossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner--file-video-portrait .Banner__video-container--file {
          max-${crossAxis}: ${crossAxisSize}px;
          max-${mainAxis}: ${crossAxisSize}px;
          height: auto;
          width: auto;
        }
      `;
  };

  const getVerticalFileVideoSlotCss = (
    crossAxis,
    crossAxisSize,
    mainAxis,
    mainAxisSize
  ) =>
    slides
      .filter(
        (banner) =>
          banner.type === BannerType.VIDEO &&
          banner.video_embed_style === BannerVideoType.FILE
      )
      .map((banner) => {
        const size = getFileVideoPosterSizeAtSlot(
          banner.video_poster_width,
          banner.video_poster_height,
          mainAxisSize,
          crossAxisSize
        );

        if (!size) {
          return '';
        }

        return `
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) {
          display: flex !important;
          align-items: center;
          justify-content: center;
          ${crossAxis}: ${crossAxisSize}px;
          ${mainAxis}: ${mainAxisSize}px;
          overflow: hidden;
        }
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .BannerSlider__slide {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .Banner--file-video,
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .Banner__wrapper,
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .Banner__wrapper-mobile,
        #${bannerSliderId} .slick-slide:has(.Banner[data-banner-id="${banner.id}"]) .MaybeExternalLink {
          width: fit-content;
          height: auto;
          max-width: 100%;
          max-height: 100%;
          align-items: center;
        }
        #${bannerSliderId} .Banner[data-banner-id="${banner.id}"] .Banner__video-container--file {
          ${crossAxis}: ${size.width}px;
          ${mainAxis}: ${size.height}px;
          aspect-ratio: auto;
        }
        #${bannerSliderId} .Banner[data-banner-id="${banner.id}"] .Banner__video-container--file video {
          object-fit: contain;
          object-position: center;
        }`;
      })
      .join('\n');

  /**
   * Generates a CSS style for the slider that restricts the maximum size
   * it may take at the current window size.
   *
   * The banner slider areas each have a preferred aspect ratio and a maximum
   * height/width (depending on the orientation). The general behavior is that
   * the slider area scales in the preferred aspect ratio until it hits the
   * maximum height or width, then only scales in the unrestricted direction
   * if it has more room. E.g. a horizontal banner slider with 2:1 aspect
   * ratio and 200 px maximum cross axis (height) size will behave as follows:
   *
   * Usable width -> Enforced size
   *    100 px    -> 100 px wide, 50 px tall (restricted by aspect ratio)
   *    400 px    -> 400 px wide, 200 px tall (restricted by aspect ratio)
   *    401 px    -> 401 px wide, 200 px tall (restricted by height)
   *    800 px    -> 800 px wide, 200 px tall (restricted by height)
   *
   * Individual image slides fill that slot height; their width follows the
   * image aspect ratio. Wider slides are cropped by the slider list edge only.
   */

  /**
   * Before a browser measurement, elementWidth is 0. Do not emit zero-pixel
   * geometry from that value. Use a responsive aspect-ratio box capped by the
   * configured maximum so the first banner stays visible in SSR/hydration.
   */
  const getUnmeasuredFallbackCss = () => {
    const localAspectRatio = getAspectRatio();
    const scentHeight = getScentHeight();
    const contentScentHeight = getContentScentHeight();
    const useMobileSquareMode = shouldUseMobileSquareMode();
    // Mobile 1:1 mode normally sizes from the measured width; without a
    // measurement keep a square aspect box and avoid inventing a viewport width.
    const maxCrossAxisSize = useMobileSquareMode
      ? null
      : getConfiguredSlotCrossAxisSize(maximumCrossAxisSize, scentHeight);
    const { bannerWrapper, bannerBackgroundImage } =
      getBannerMobileCssClasses();

    return `
        #${bannerSliderId},
        #${bannerSliderId} .BannerSlider__fallback {
          width: 100%;
          max-width: 100%;
        }
        #${bannerSliderId} .BannerSlider__fallback {
          position: relative;
          overflow: hidden;
          aspect-ratio: ${localAspectRatio};${
      maxCrossAxisSize
        ? `
          max-height: ${maxCrossAxisSize}px;`
        : ''
    }
        }
        #${bannerSliderId} .BannerSlider__fallback .BannerSlider__slide,
        #${bannerSliderId} .BannerSlider__fallback .Banner,
        #${bannerSliderId} .BannerSlider__fallback ${bannerWrapper},
        #${bannerSliderId} .BannerSlider__fallback .MaybeExternalLink,
        #${bannerSliderId} .BannerSlider__fallback picture {
          display: block;
          width: 100%;
          height: 100%;
          max-width: 100%;
          margin: 0;
          padding: 0;
        }
        #${bannerSliderId} .BannerSlider__fallback ${bannerBackgroundImage} {
          display: block;
          width: 100%;
          height: 100%;
          max-width: 100%;
          object-fit: cover;
          object-position: center;
        }
        #${bannerSliderId} .BannerSlider__fallback .Banner__overlay {
          max-height: calc(100% - ${contentScentHeight}px);
        }
        #${bannerSliderId} .BannerSlider__fallback .Banner__video-container:not(.Banner__video-container--file) {
          width: 100%;
          height: 100%;
          aspect-ratio: ${VIDEO_ASPECT_RATIO};
          max-height: 100%;
        }
      `;
  };

  const getCSS = useMemo(() => {
    const localAspectRatio = getAspectRatio();
    const localMaximumCrossAxisSize = getMaximumHeight();
    const scentHeight = getScentHeight();
    const contentScentHeight = getContentScentHeight();
    const mainAxis = getMainAxis();
    const crossAxis = getCrossAxis();

    if (!elementWidth) {
      return getUnmeasuredFallbackCss();
    }

    if (mainAxis === 'width') {
      const mainAxisSize = countMainAxisSize(elementWidth, localAspectRatio);
      const crossAxisSize = countMinCrossAxisSize(
        localMaximumCrossAxisSize,
        mainAxisSize
      );
      const mainAxisDefaultSize = countMainAxisDefaultSize(
        crossAxisSize,
        localAspectRatio
      );

      const embedVideoMainAxisSize = countMainAxisDefaultSize(
        crossAxisSize,
        VIDEO_ASPECT_RATIO
      );

      const { bannerWrapper, bannerBackgroundImage } =
        getBannerMobileCssClasses();

      return `
        #${bannerSliderId} .BannerSlider__slide .Banner:not(.Banner--file-video) ${bannerWrapper},
        #${bannerSliderId} .BannerSlider__slide .Banner:not(.Banner--file-video) ${bannerBackgroundImage} {
          ${crossAxis}: ${crossAxisSize}px;
          min-${mainAxis}: ${crossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner:not(.Banner--file-video) .Banner__overlay {
          ${crossAxis}: ${crossAxisSize - contentScentHeight}px;
          min-${mainAxis}: ${crossAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner__video-container:not(.Banner__video-container--file) {
          ${crossAxis}: ${crossAxisSize}px;
          ${mainAxis}: ${embedVideoMainAxisSize}px;
        }
        #${bannerSliderId} .Slider,
        #${bannerSliderId} .slick-slider,
        #${bannerSliderId} .slick-list,
        #${bannerSliderId} .slick-slide,
        #${bannerSliderId} .BannerSlider__slide,
        #${bannerSliderId} .Banner,
        #${bannerSliderId} .slick-track {
          max-${crossAxis}: ${getConfiguredSlotCrossAxisSize(
        crossAxisSize,
        scentHeight
      )}px;
        }
        #${bannerSliderId} .BannerSlider__slide:not(:has(.Banner--file-video)) {
          max-${mainAxis}: none;
        }
        #${bannerSliderId} .BannerSlider__slide .Banner--default-width {
          ${mainAxis}: ${mainAxisDefaultSize}px;
        }
        ${getConfiguredSlotCss(crossAxis, crossAxisSize, mainAxis, scentHeight)}
        ${getImageSlideWidthCss(
          crossAxis,
          crossAxisSize,
          mainAxis,
          contentScentHeight,
          maximumSlideWidth || elementWidth
        )}
        ${getLinkVideoSlideWidthCss(
          crossAxis,
          crossAxisSize,
          mainAxis,
          maximumSlideWidth || elementWidth
        )}
        ${getFileVideoSlideCss(
          crossAxis,
          crossAxisSize,
          mainAxis,
          maximumSlideWidth || elementWidth,
          scentHeight
        )}
        ${getFileVideoPosterSizedCss(
          crossAxis,
          crossAxisSize,
          mainAxis,
          maximumSlideWidth || elementWidth
        )}
        ${getPortraitFileVideoSquareSlotCss(crossAxis, crossAxisSize, mainAxis)}
        `;
    } else {
      const crossAxisSize = countMinCrossAxisSize(
        localMaximumCrossAxisSize,
        getCrossAxisSize()
      );
      const mainAxisSize = countMainAxisSize(crossAxisSize, localAspectRatio);
      const sliderSizeByMainAxis = mainAxisSize * slidesToShow;

      return `
        #${bannerSliderId} .BannerSlider__slide img {
          max-${crossAxis}: ${crossAxisSize}px;
          max-${mainAxis}: ${mainAxisSize}px;
        }
        #${bannerSliderId} .BannerSlider__slide,
        #${bannerSliderId} .slick-slide {
          ${mainAxis}: ${mainAxisSize}px;
        }

        #${bannerSliderId} .slick-slider,
        #${bannerSliderId} .slick-list,
        #${bannerSliderId} .slick-track {
          ${mainAxis}: ${sliderSizeByMainAxis}px !important;
        }
        ${getVerticalFileVideoSlotCss(
          crossAxis,
          crossAxisSize,
          mainAxis,
          mainAxisSize
        )}
        `;
      // Upper slick-block is a fix for react-slider slider height bug, if there's equal or less amount of images that are set in slidesToShow
      // slidesToScroll amount seems to also affect somehow to the height calculation
    }
  }, [
    slides,
    bannerSlider,
    bannerSliderId,
    uiStore.isSmallest,
    uiStore.isMobile,
    adZone,
    isConfiguredSlotHeight,
    elementWidth,
    maximumSlideWidth,
    maximumCrossAxisSize,
    aspectRatio,
    ifVertical,
    slidesToShow,
    configStore.banner.scent.pattern,
    configStore.banner.scent.shouldShowMobile,
    configStore.banner.scent.onTop,
    configStore.banner.scent.height,
    countMainAxisDefaultSize,
    contentJustify,
    useCreativeSlideWidth,
    measuredImageSizes,
  ]);

  return <style>{getCSS}</style>;
};

BannerSliderStyles.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  uiStore: modelOf(UIStore).isRequired,
  aspectRatio: PropTypes.number.isRequired,
  maximumCrossAxisSize: PropTypes.number.isRequired,
  adZone: PropTypes.string,
  slides: PropTypes.arrayOf(modelOf(Ad)),
  bannerSliderId: PropTypes.string,
  elementWidth: PropTypes.number,
  maximumSlideWidth: PropTypes.number,
  useCreativeSlideWidth: PropTypes.bool,
  measuredImageSizes: PropTypes.object,
  ifVertical: PropTypes.bool,
  slidesToShow: PropTypes.number,
  bannerSlider: PropTypes.object,
  slideContentJustify: PropTypes.oneOf(['flex-start', 'center']),
};

export default inject('configStore', 'uiStore')(observer(BannerSliderStyles));
